import { LoggerFactory, Logger } from './../../core/logger.service';
import { TriggerbeeRequester } from '../../core/triggerbee/triggerbee-requester.service';
import { Injectable } from '@angular/core';
import { TriggerbeeApi } from '../../core/triggerbee/triggerbee-api.enum';
import { Observable } from 'rxjs';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { Account } from 'app/shared/account/account.model';
import { OngoingVisitSession } from './ongoing-visit-session.model';

@Injectable()
export class TrackerService {
  // Where the server-side cookie setup guide mounts the customer's first-party proxy.
  public static readonly firstPartyProxyPath = '/tb';

  private readonly logger: Logger;

  constructor(
    private readonly requester: TriggerbeeRequester,
    private readonly http: HttpRequestDecorator,
    loggerFactory: LoggerFactory,
  ) {
    this.logger = loggerFactory.get('TrackerService');
  }

  public async getAccounts(): Promise<any> {
    const response = await this.requester.getAsync<any>(TriggerbeeApi.Gateway, 'tracker/accounts');
    if (!response.isPositive()) {
      this.logger.error(response.error);
      return [];
    }
    return response.data;
  }

  public async getLatestVisitor(): Promise<any> {
    const response = await this.requester.getAsync<any>(TriggerbeeApi.Gateway, 'tracker/latest-visit');
    if (response.error && response.statusCode === 200) {
      return response.error?.text;
    } else if (!response.isPositive()) {
      this.logger.error(response.error);
      return null;
    }
    return response.data;
  }

  public async getCurrentVisitors(): Promise<any> {
    const response = await this.requester.getAsync<any>(TriggerbeeApi.Gateway, 'tracker/current-visitors');
    if (!response.isPositive()) {
      this.logger.error(response.error);
      return null;
    }
    return response.data;
  }

  public getCurrentSiteVisitCount(): Observable<number> {
    return this.http.get(`${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/tracker/site-current-visitors`);
  }

  /** The current site's cookie-free visits DataPlatform has not stored yet, except those merged into a consented one. */
  public getOngoingStatelessVisits(take: number): Observable<OngoingVisitSession[]> {
    return this.http.get(`${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/visitor-sessions/ongoing-stateless`, { take });
  }

  public static getTrackerBaseUrl(account: Account, trackerDomain: string): string {
    const primaryDomain = account?.useServersideCookie
      ? account.urls?.[0]?.replace(/^(https?:)?\/\//i, '').replace(/\/+$/, '')
      : null;
    // Always https: the uid cookie is Secure, so the browser drops it on an http response.
    return primaryDomain ? `https://${primaryDomain}${TrackerService.firstPartyProxyPath}` : trackerDomain;
  }

  public static getTrackingScript(siteId: number, trackerBaseUrl: string): string {
    if (!trackerBaseUrl) {
      throw Error('Tracker base URL is required');
    }
    const scriptBaseUrl =
      trackerBaseUrl.startsWith('http') || trackerBaseUrl.startsWith('//') ? trackerBaseUrl : '//' + trackerBaseUrl;
    return (
      'var mtr_custom = mtr_custom || {};\n' +
      'var mtr_site_id = ' +
      siteId +
      ';\n' +
      '(function () {\n' +
      "     var s = document.createElement('script'); s.async = true; s.src = '" +
      scriptBaseUrl +
      "/js?site_id=' + mtr_site_id;\n" +
      "     (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(s);\n" +
      "     var sc = document.createElement('script'); sc.async = true; sc.src = '" +
      scriptBaseUrl +
      "/js/' + mtr_site_id + (window.location.href.indexOf('tb-nocache') > -1 ? '?v=' + Math.floor(Math.random()*999) : '');\n" +
      "     (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(sc);\n" +
      '})();'
    );
  }
}
